import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { formatDate } from '../../utils/dateUtils';
import ShipDateService from '../setting/shipDate/ShipDateService';
import CaseTicketService from './CaseTicketService';
import { toDateOnlyString } from './constants';

const REAL = (t) => !!t && t !== 'ไม่มีเลข' && t !== '-';

// The case is waiting on Japan. The warehouse is NOT in our LINE group, so the
// admin asks them through whatever channel they use — this panel shows what
// they need to quote (tracking, the name on the box, which site it came from)
// and takes the answer back per parcel: they routinely find some and not others.
//
// "Found" is not the end: a found parcel must name the round it travels on, and
// carry a tracking number, or the customer still has nothing to be told.
//
// Anyone may do it — there is no named coordinator, and the case claim already
// stops two admins working the same case.
//
// A parcel Japan already answered "not found" stays here with only ✓ เจอ: it
// often turns up later, and an untracked one can never be found by the sweep.
// With nothing left for Japan to answer, the panel folds down to one button.
const WarehousePanel = ({ caseTicket, items, onDone }) => {
  const [answers, setAnswers] = useState({}); // itemId -> { status, shipDateId, trackingJp }
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [shipDates, setShipDates] = useState([]);
  const [open, setOpen] = useState(false);

  // Japan answers as they look, so a parcel they have not reached yet must not
  // block recording the ones they have.
  const pending = items.filter((it) => it.foundStatus === 'pending');
  const waiting = pending.filter((it) => !it.warehouseAnsweredAt);
  const answeredMissing = pending.filter((it) => it.warehouseAnsweredAt);
  const rows = [...waiting, ...answeredMissing];

  // The full round list, not the `operational` one: that caps at 7 days ahead,
  // which left almost nothing to pick from once next month's rounds existed.
  // Cancelled rounds are dropped here instead, and each dropdown narrows it.
  useEffect(() => {
    ShipDateService.getShipDatesManage()
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        setShipDates(
          rows.filter((s) => s.active !== false)
            .sort((a, b) => String(b.shipDate).localeCompare(String(a.shipDate))) // newest first
        );
      })
      .catch(() => toast.error('โหลดรอบเรือ/แอร์ไม่สำเร็จ'));
  }, []);

  const today = toDateOnlyString(new Date());

  /**
   * Which rounds a parcel could plausibly travel on: never one that sailed
   * before it was ordered, and never one so far afterwards that it could not
   * still be the same parcel — 4 months by sea, 2 by air.
   */
  const roundsFor = (item) => {
    const ordered = item.orderDate ? String(item.orderDate).slice(0, 10) : null;
    if (!ordered) return shipDates;
    return shipDates.filter((s) => {
      const sails = String(s.shipDate).slice(0, 10);
      if (sails < ordered) return false;
      const limit = new Date(ordered);
      limit.setMonth(limit.getMonth() + (s.isAir ? 2 : 4));
      return sails <= toDateOnlyString(limit);
    });
  };
  // A found parcel travelled on one of those that has already sailed.
  const goneRoundsFor = (item) => roundsFor(item).filter((s) => String(s.shipDate).slice(0, 10) <= today);

  const set = (itemId, patch) => setAnswers((prev) => ({ ...prev, [itemId]: { ...prev[itemId], ...patch } }));

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`คัดลอก ${text}`)).catch(() => {});
  };

  // Only the rows answered right now are sent. A found parcel must name the
  // round it travels on; a tracking number is welcome but optional, since the
  // warehouse finds untracked parcels by the name on the box.
  // ส่งผิดขนส่ง needs the round it left Japan on AND the central warehouse told.
  const complete = (a) => (a?.status === 'found' && !!a.shipDateId)
    || (a?.status === 'not_found' && !!a.expectedShipDateId)
    || (a?.status === 'misrouted' && !!a.shipDateId && a.centerInformed === true);
  const toSend = rows.filter((it) => complete(answers[it.id]));
  const incomplete = rows.some((it) => answers[it.id]?.status && !complete(answers[it.id]));

  const submit = async () => {
    const foundCount = toSend.filter((it) => answers[it.id].status === 'found').length;
    const misroutedCount = toSend.filter((it) => answers[it.id].status === 'misrouted').length;
    const missCount = toSend.length - foundCount - misroutedCount;
    const left = waiting.filter((it) => !toSend.includes(it)).length;
    const confirmText = [
      `บันทึก ${toSend.length} รายการ — เจอ ${foundCount} · ไม่เจอ ${missCount}${misroutedCount ? ` · ส่งผิดขนส่ง ${misroutedCount}` : ''}`,
      left > 0 ? `ยังเหลืออีก ${left} รายการที่ยังไม่ได้ผลจาก Kluay (บันทึกทีหลังได้)` : null,
      'ยืนยันไหม?',
    ].filter(Boolean).join('\n');
    if (!window.confirm(confirmText)) return;

    const payload = Object.fromEntries(toSend.map((it) => [it.id, answers[it.id]]));
    setSaving(true);
    try {
      await CaseTicketService.warehouseResult(caseTicket.id, payload, note.trim() || undefined);
      toast.success('บันทึกผลจาก Kluay แล้ว');
      setOpen(false);
      setAnswers({});
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  if (waiting.length === 0 && !open) {
    return (
      <div className="border border-orange-200 bg-orange-50 rounded-lg px-4 py-2 mb-4 flex flex-wrap items-center gap-2">
        <span className="text-sm text-orange-900">
          🔎 Kluay ตอบไม่เจอ {answeredMissing.length} รายการ — ยังตามหาอยู่
        </span>
        <button type="button" onClick={() => setOpen(true)}
          className="ml-auto text-sm px-3 py-1 rounded-full border border-green-600 text-green-700 bg-white font-medium hover:bg-green-50">
          ✓ เจอของแล้ว
        </button>
      </div>
    );
  }

  return (
    <div className="border-2 border-orange-400 bg-orange-50 rounded-lg p-4 mb-4">
      <h3 className="font-bold text-orange-900 mb-1">
        {waiting.length > 0 ? '📦 รอผลจาก Kluay' : '🔎 เจอของที่ตามหาแล้ว'}
        {waiting.length > 0 && <span className="ml-2 text-sm font-normal">({waiting.length} รายการยังไม่ได้ผล)</span>}
      </h3>
      <p className="text-sm text-orange-800 mb-3">
        {waiting.length > 0 ? (
          <>
            ถาม Kluay ด้วยข้อมูลด้านล่าง แล้วกลับมาบันทึกผล — ใครก็ได้ที่ว่างทำได้ ·
            <b> บันทึกทีละรายการได้</b> ไม่ต้องรอให้ครบ · เจอ = ระบุ<b>รอบที่ของมา</b> ·
            ไม่เจอ = ระบุ<b>รอบที่คาดว่าจะมา</b> (กำหนดคืนเงินนับจากรอบนี้)
          </>
        ) : (
          <>กด ✓ เจอ ที่รายการที่เจอ แล้วเลือก<b>รอบที่ของมา</b> — ระบบร่างข้อความแจ้งลูกค้าให้ (ส่วนลดค่าน้ำหนักที่สัญญาไว้ยังอยู่)</>
        )}
      </p>

      <div className="flex flex-col gap-2 mb-3">
        {rows.map((it) => {
          const a = answers[it.id] || {};
          const hasTracking = REAL(it.trackingJp);
          const late = !!it.warehouseAnsweredAt;
          return (
            <div key={it.id} className="bg-white border border-orange-100 rounded p-2">
              <div className="flex flex-wrap items-center gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm flex items-center gap-2 min-w-0">
                    <a href={it.url} target="_blank" rel="noreferrer" className="text-blue-700 underline truncate">{it.url}</a>
                    {it.order?.website && <span className="text-xs bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded shrink-0">{it.order.website}</span>}
                    {it.priceYen != null && <span className="text-xs text-gray-500 shrink-0">{Number(it.priceYen).toLocaleString()} เยน</span>}
                  </div>
                  <div className="text-xs text-gray-600 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span>
                      ชื่อกล่อง:{' '}
                      <button type="button" onClick={() => copy(it.order?.smileNameInput || '')}
                        className="font-mono underline decoration-dotted hover:text-blue-700">
                        {it.order?.smileNameInput || '—'}
                      </button>
                    </span>
                    <span>
                      เลขพัสดุ:{' '}
                      {hasTracking ? (
                        <button type="button" onClick={() => copy(it.trackingJp)}
                          className="font-mono underline decoration-dotted hover:text-blue-700">
                          {it.trackingJp}
                        </button>
                      ) : <span className="text-red-600">ไม่มีเลข</span>}
                    </span>
                  </div>
                </div>

                {late && <span className="text-xs text-gray-500">Kluay ตอบไม่เจอไปแล้ว</span>}
                {[
                  { v: 'found', label: '✓ เจอ', on: 'bg-green-600 text-white border-green-600', off: 'text-green-700 border-green-300' },
                  { v: 'not_found', label: '✕ ไม่เจอ', on: 'bg-red-600 text-white border-red-600', off: 'text-red-700 border-red-300' },
                  { v: 'misrouted', label: '📦 ส่งผิดขนส่ง', on: 'bg-amber-600 text-white border-amber-600', off: 'text-amber-700 border-amber-300' },
                ].filter((b) => !late || b.v === 'found').map((b) => (
                  <button key={b.v} type="button" onClick={() => set(it.id, { status: b.v })}
                    className={`text-xs px-3 py-1 rounded-full border font-medium ${a.status === b.v ? b.on : `bg-white ${b.off}`}`}>
                    {b.label}
                  </button>
                ))}
              </div>

              {/* Found → which round it actually travelled on. A late find may
                  still be waiting in Japan, so its round may not have sailed yet. */}
              {a.status === 'found' && (
                <div className="grid sm:grid-cols-2 gap-2 mt-2 pt-2 border-t border-orange-100">
                  <FormControl fullWidth size="small">
                    <InputLabel id={`round-${it.id}`}>รอบที่ของมา *</InputLabel>
                    <Select labelId={`round-${it.id}`} label="รอบที่ของมา *" value={a.shipDateId || ''}
                      onChange={(e) => set(it.id, { shipDateId: e.target.value })}>
                      {(late ? roundsFor(it) : goneRoundsFor(it)).map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.isAir ? '✈️' : '🚢'} {formatDate(s.shipDate)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {!hasTracking && (
                    <TextField size="small" label="เลขพัสดุที่ Kluay แจ้ง (ถ้ามี)" value={a.trackingJp || ''}
                      onChange={(e) => set(it.id, { trackingJp: e.target.value })} />
                  )}
                </div>
              )}

              {/* ส่งผิดขนส่ง → found on a round, but sorted to another shipper. The
                  round it left Japan on, and the central warehouse told to fetch it. */}
              {a.status === 'misrouted' && (
                <div className="mt-2 pt-2 border-t border-orange-100 flex flex-col gap-2">
                  <FormControl fullWidth size="small">
                    <InputLabel id={`misroute-${it.id}`}>รอบที่ส่งออกจากญี่ปุ่น *</InputLabel>
                    <Select labelId={`misroute-${it.id}`} label="รอบที่ส่งออกจากญี่ปุ่น *" value={a.shipDateId || ''}
                      onChange={(e) => set(it.id, { shipDateId: e.target.value })}>
                      {roundsFor(it).map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.isAir ? '✈️' : '🚢'} {formatDate(s.shipDate)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <label className="flex items-center gap-2 text-sm text-amber-800">
                    <input type="checkbox" checked={a.centerInformed === true}
                      onChange={(e) => set(it.id, { centerInformed: e.target.checked })} />
                    แจ้งโกดังกลางให้ตามของกลับแล้ว *
                  </label>
                </div>
              )}

              {/* Not found → which round we now expect it on. This is a shipping
                  call the admin can make, and it is what the refund deadline
                  counts from, so the supervisor never has to work it out. */}
              {a.status === 'not_found' && (
                <div className="mt-2 pt-2 border-t border-orange-100">
                  <FormControl fullWidth size="small">
                    <InputLabel id={`expect-${it.id}`}>รอบที่คาดว่าของจะมา *</InputLabel>
                    <Select labelId={`expect-${it.id}`} label="รอบที่คาดว่าของจะมา *" value={a.expectedShipDateId || ''}
                      onChange={(e) => set(it.id, { expectedShipDateId: e.target.value })}>
                      {roundsFor(it).map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.isAir ? '✈️' : '🚢'} {formatDate(s.shipDate)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <TextField fullWidth size="small" multiline minRows={1} sx={{ bgcolor: 'white', mb: 2 }}
        label={waiting.length > 0 ? 'Kluay บอกอะไรเพิ่ม (ไม่บังคับ)' : 'เจอที่ไหน / หมายเหตุ (ไม่บังคับ)'} value={note} onChange={(e) => setNote(e.target.value)} />

      <button onClick={submit} disabled={saving || toSend.length === 0}
        className="bg-orange-600 hover:bg-orange-700 disabled:opacity-40 text-white px-4 py-2 rounded-lg font-medium">
        บันทึกผลจาก Kluay{toSend.length > 0 ? ` (${toSend.length} รายการ)` : ''}
      </button>
      {waiting.length === 0 && (
        <button type="button" onClick={() => { setOpen(false); setAnswers({}); }}
          className="ml-3 text-sm text-gray-600 underline">ยกเลิก</button>
      )}
      {incomplete && (
        <p className="text-xs text-red-600 mt-1">รายการที่กด "เจอ" ต้องเลือกรอบก่อน ถึงจะบันทึกได้</p>
      )}
    </div>
  );
};

export default WarehousePanel;
